import React, { ChangeEvent, useEffect, useState } from "react";
import styled from "styled-components";

interface SLabel {
  focused: boolean;
  filled: boolean;
}

interface SInput {
  hasError?: boolean;
}

interface SIcon extends SInput {
  isPassword?: boolean;
}

interface LoginInputProps extends SIcon {
  label: string;
  value: string;
  onChangeEvent: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyUpEvent?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  textType?: string;
  inputMode?:
    | "text"
    | "search"
    | "email"
    | "tel"
    | "url"
    | "none"
    | "numeric"
    | "decimal";
}

export default function LoginInput({
  label,
  value,
  onChangeEvent,
  isPassword,
  hasError,
  onKeyUpEvent,
  textType,
  inputMode,
}: LoginInputProps) {
  const [focused, setFocused] = useState(false);
  const [filled, setFilled] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (value?.length > 0) {
      setFilled(true);
      setFocused(true);
    }
  }, [value]);

  return (
    <LoginInputContainer>
      <Icon
        hasError={hasError}
        isPassword={isPassword}
        className={
          isPassword ? "material-symbols-outlined" : "material-icons-outlined"
        }
      >
        {isPassword ? "key" : "email"}
      </Icon>

      <Label focused={focused} filled={filled}>
        {label}
      </Label>

      <Input
        hasError={hasError}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          if (value === "") {
            setFilled(false);
            setFocused(false);
          } else {
            setFilled(true);
          }
        }}
        onChange={(e) => onChangeEvent(e)}
        onKeyUp={(e) => onKeyUpEvent && onKeyUpEvent(e)}
        value={value}
        type={
          textType ?? (isPassword ? (visible ? "text" : "password") : "email")
        }
        inputMode={inputMode}
      />

      {(textType ? textType === "password" : isPassword) && (
        <VisibleIcon
          className="material-icons-outlined"
          onClick={() => setVisible(!visible)}
        >
          {visible ? "visibility_off" : "visibility"}
        </VisibleIcon>
      )}
    </LoginInputContainer>
  );
}

const LoginInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Icon = styled.span<SIcon>`
  position: absolute;
  left: 16px;
  font-size: 24px;
  color: ${({ hasError, theme }) =>
    hasError ? theme.buttonColor.redNormal : "#858c97"};
  transform: ${({ isPassword }) => (isPassword ? "rotate(-45deg)" : "none")};
`;

const Label = styled.label<SLabel>`
  position: absolute;
  left: 52px;

  font-size: ${({ focused, filled }) => (filled || focused ? 12 : 14)}px;
  text-align: left;
  color: #858c97;

  transform: ${({ focused, filled }) =>
    filled || focused ? `translateY(-8px)` : `none`};
  transition: font-size 0.2s ease-out, transform 0.15s ease-out;
`;

const Input = styled.input<SInput>`
  width: 100%;
  height: 55px;
  border-radius: 5px;
  border: solid 1px
    ${({ hasError, theme }) =>
      hasError ? theme.buttonColor.redNormal : theme.buttonColor.lineNormal};
  background-color: #ffffff;
  padding: 22px 36px 0 52px;

  font-size: 14px;
  line-height: 21px;
  text-align: left;
  color: ${({ theme }) => theme.color.textColor};

  &:disabled {
    border-color: ${({ theme }) => theme.buttonColor.blueDisabled};
  }

  &:focus {
    border-color: ${({ theme, hasError }) =>
      hasError ? theme.buttonColor.redNormal : theme.buttonColor.blueHover};
  }
`;

const VisibleIcon = styled.span`
  cursor: pointer;
  position: absolute;
  right: 18px;

  font-size: 18px;
  color: #c2cad4;
`;
